import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, CardContent, Container, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../auth/useAuth';
import { getOrg } from '../../api/admin';
import SafeHtml from '../../components/SafeHtml';
import ParticipantBottomNav from '../../components/ParticipantBottomNav';
import { BOTTOM_NAV_HEIGHT, getLastRunId } from '../../components/participantNav';
import logoSmall from '../../assets/logos/logo-small.svg';

type LegalSlug = 'imprint' | 'privacy' | 'accessibility';

interface LegalShellProps {
  title: string;
  /** Which org column to look up for an admin override. */
  slug?: LegalSlug;
  children: React.ReactNode;
}

/**
 * Lightweight wrapper for static legal pages. Centred card with the
 * brand mark on top and a prominent "Back" CTA. Reachable from the
 * participant overflow menu and the admin sidebar footer.
 *
 * When the calling user is authenticated AND their organisation has a
 * non-null Markdown override for the matching `slug`, that override
 * replaces the bundled `children` copy. Falling back keeps the
 * placeholder useful for unauthenticated visitors and for orgs that
 * haven't filled their own copy yet.
 */
const LegalShell: React.FC<LegalShellProps> = ({ title, slug, children }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [override, setOverride] = useState<string | null>(null);

  useEffect(() => {
    if (!slug || !user?.organization_slug) return;
    let cancelled = false;
    void getOrg(user.organization_slug)
      .then((org) => {
        if (cancelled) return;
        const map = {
          imprint: org.legal_imprint,
          privacy: org.legal_privacy,
          accessibility: org.legal_accessibility,
        };
        setOverride(map[slug] ?? null);
      })
      .catch(() => {
        // ignore — fall back to bundled copy
      });
    return () => {
      cancelled = true;
    };
  }, [slug, user?.organization_slug]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard');
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100dvh',
        pt: 4,
        pb: `${BOTTOM_NAV_HEIGHT + 32}px`,
      }}
    >
      <Container maxWidth="md">
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', mb: 3 }}
          component={RouterLink}
          to="/"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <Box component="img" src={logoSmall} alt="Stadtrallye" sx={{ width: 32, height: 28 }} />
          <Typography variant="h6" component="span">
            Stadtrallye
          </Typography>
        </Stack>

        <Card>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="h2" component="h1" gutterBottom>
              {title}
            </Typography>
            <Box sx={{ '& p': { mb: 2 }, '& h2': { mt: 3, mb: 1 } }}>
              {override ? <SafeHtml html={override} /> : children}
            </Box>
            <Box sx={{ mt: 4 }}>
              <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={goBack}>
                {t('actions.back')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
      <ParticipantBottomNav runId={getLastRunId()} active={false} />
    </Box>
  );
};

export default LegalShell;
